import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, Globe, Clock, CheckCircle, ArrowRight, Shield, CreditCard, FileText, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";

const appPackages = [
  {
    id: "app_starter", name: "Starter App", price: 2499, deposit: 1249.50,
    description: "Perfect for MVPs and simple apps", timeline: "2-4 weeks", color: "#A0A0AB",
    features: ["Single-platform (iOS or Android)", "Up to 5 core screens", "Basic UI/UX design", "User authentication", "Push notifications", "App Store / Play Store submission", "30 days post-launch support"],
  },
  {
    id: "app_pro", name: "Professional App", price: 5999, deposit: 2999.50, popular: true,
    description: "Full-featured apps for growing businesses", timeline: "4-8 weeks", color: "#00F0FF",
    features: ["Cross-platform (iOS + Android)", "Up to 15 screens", "Custom UI/UX design", "User auth + social login", "API integrations (payments, maps, etc.)", "Admin dashboard", "Push notifications + in-app messaging", "Analytics integration", "App Store + Play Store submission", "60 days post-launch support"],
  },
  {
    id: "app_enterprise", name: "Enterprise App", price: 14999, deposit: 7499.50,
    description: "Complex, scalable applications", timeline: "8-16 weeks", color: "#FF007F",
    features: ["Cross-platform (iOS + Android + Web)", "Unlimited screens", "Premium UI/UX + branding", "Advanced auth + role management", "AI/ML integration", "Real-time features (chat, notifications)", "Payment processing", "HIPAA / SOC2 compliance ready", "Custom admin panel", "CI/CD pipeline setup", "All store submissions", "90 days support + SLA"],
  },
];

const webPackages = [
  {
    id: "web_starter", name: "Business Website", price: 999, deposit: 499.50,
    description: "Professional web presence", timeline: "1-2 weeks", color: "#A0A0AB",
    features: ["Up to 5 pages", "Responsive design (mobile + desktop)", "Contact form", "SEO optimization", "Google Analytics setup", "SSL certificate", "Domain setup assistance", "30 days support"],
  },
  {
    id: "web_pro", name: "Advanced Website", price: 2999, deposit: 1499.50, popular: true,
    description: "Dynamic sites with CMS and integrations", timeline: "2-4 weeks", color: "#00F0FF",
    features: ["Up to 15 pages", "Custom design + animations", "Content management system (CMS)", "Blog / News section", "Email integration", "Social media integration", "Advanced SEO + Open Graph", "Speed optimization", "60 days support"],
  },
  {
    id: "web_ecommerce", name: "E-Commerce / Marketplace", price: 7999, deposit: 3999.50,
    description: "Full online store or marketplace", timeline: "4-8 weeks", color: "#FF007F",
    features: ["Unlimited pages", "Premium custom design", "Product catalog + inventory", "Shopping cart + checkout", "Payment processing (Stripe)", "User accounts + dashboards", "Order management system", "Email notifications", "Analytics dashboard", "90 days support + SLA"],
  },
];

export default function ServicesPricing() {
  const [activeTab, setActiveTab] = useState("apps");
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [step, setStep] = useState("select"); // select, form, terms, paying
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", project_description: "" });
  const [agreesToTerms, setAgreesToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const packages = activeTab === "apps" ? appPackages : webPackages;

  const handleSelectPackage = (pkg) => {
    setSelectedPkg(pkg);
    setStep("form");
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_description) {
      setError("Please fill in all required fields.");
      return;
    }
    setStep("terms");
    setError("");
  };

  const handlePayDeposit = async () => {
    if (!agreesToTerms) { setError("You must agree to the terms and conditions."); return; }
    setLoading(true);
    setError("");
    try {
      const booking = await api.createServiceBooking({
        ...form, package_id: selectedPkg.id, agrees_to_terms: true,
      });
      const checkout = await api.createServiceCheckout(booking.id, window.location.origin);
      window.location.href = checkout.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to process. Please try again.");
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 transition-all text-sm";

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="services-pricing-page">
      <Header />
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">

          {/* Back button when in form/terms */}
          {step !== "select" && (
            <button
              data-testid="pricing-back"
              onClick={() => { if (step === "terms") setStep("form"); else { setStep("select"); setSelectedPkg(null); } }}
              className="flex items-center gap-2 text-[#A0A0AB] hover:text-[#00F0FF] transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}

          {/* STEP 1: Package Selection */}
          <AnimatePresence mode="wait">
            {step === "select" && (
              <motion.div key="select" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="text-center mb-12">
                  <p className="label-mono mb-4">Service Pricing</p>
                  <h1 className="font-['Unbounded'] text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4">
                    Choose Your <span className="bg-gradient-to-r from-[#00F0FF] to-[#FF007F] bg-clip-text text-transparent">Package</span>
                  </h1>
                  <p className="text-[#A0A0AB] text-base max-w-xl mx-auto">Select a package, tell us about your project, and pay a 50% deposit to get started. Fixed pricing — no surprises.</p>
                </div>

                {/* Tabs */}
                <div className="flex justify-center mb-10">
                  <div className="inline-flex gap-2 p-1.5 bg-white/5 rounded-2xl border border-white/10">
                    <button data-testid="pkg-tab-apps" onClick={() => setActiveTab("apps")}
                      className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all ${activeTab === "apps" ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30" : "text-[#A0A0AB]"}`}>
                      <Smartphone className="w-4 h-4" /> App Development
                    </button>
                    <button data-testid="pkg-tab-web" onClick={() => setActiveTab("web")}
                      className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all ${activeTab === "web" ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30" : "text-[#A0A0AB]"}`}>
                      <Globe className="w-4 h-4" /> Web Development
                    </button>
                  </div>
                </div>

                {/* Packages */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                  {packages.map((pkg, i) => (
                    <motion.div key={pkg.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                      data-testid={`pkg-card-${pkg.id}`}
                      className={`glass-card rounded-3xl p-7 relative flex flex-col ${pkg.popular ? "border-[#00F0FF]/40 shadow-[0_0_30px_rgba(0,240,255,0.1)]" : ""}`}>
                      {pkg.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00F0FF] text-black text-[10px] font-bold px-4 py-1 rounded-full">MOST POPULAR</div>}
                      <h3 className="font-['Unbounded'] text-xl font-bold text-white mb-1">{pkg.name}</h3>
                      <p className="text-[#A0A0AB] text-xs mb-4">{pkg.description}</p>
                      <div className="mb-1">
                        <span className="text-4xl font-black text-white">${pkg.price.toLocaleString()}</span>
                        <span className="text-[#A0A0AB] text-sm ml-1">total</span>
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[#00F0FF] text-sm font-semibold">${pkg.deposit.toLocaleString()} deposit</span>
                        <span className="text-[#A0A0AB] text-xs">(50% upfront)</span>
                      </div>
                      <div className="flex items-center gap-2 mb-6">
                        <Clock className="w-4 h-4" style={{ color: pkg.color }} />
                        <span className="text-sm" style={{ color: pkg.color }}>{pkg.timeline}</span>
                      </div>
                      <ul className="space-y-2 mb-8 flex-1">
                        {pkg.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: pkg.color }} />
                            <span className="text-[#A0A0AB]">{f}</span>
                          </li>
                        ))}
                      </ul>
                      <button data-testid={`select-pkg-${pkg.id}`} onClick={() => handleSelectPackage(pkg)}
                        className={`w-full text-center py-3.5 rounded-full text-sm font-semibold transition-all ${pkg.popular ? "bg-[#00F0FF] text-black hover:shadow-[0_0_25px_rgba(0,240,255,0.6)]" : "border border-white/20 text-white hover:border-white/40"}`}>
                        Select & Continue <ArrowRight className="w-4 h-4 inline ml-1" />
                      </button>
                    </motion.div>
                  ))}
                </div>

                {/* Payment Info */}
                <div className="glass-card rounded-2xl p-6 max-w-2xl mx-auto text-center">
                  <div className="flex items-center justify-center gap-2 text-[#A0A0AB] text-sm mb-2">
                    <Shield className="w-4 h-4 text-[#00F0FF]" /> How it works
                  </div>
                  <p className="text-[#A0A0AB] text-xs">Pay a <span className="text-white font-semibold">50% deposit</span> to start your project. The remaining 50% is due upon project completion and approval. All payments are securely processed via Stripe.</p>
                </div>
              </motion.div>
            )}

            {/* STEP 2: Client Info Form */}
            {step === "form" && selectedPkg && (
              <motion.div key="form" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
                <div className="max-w-2xl mx-auto">
                  {/* Selected Package Summary */}
                  <div className="glass-card rounded-2xl p-5 mb-8 flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <p className="text-xs text-[#00F0FF] font-['JetBrains_Mono'] uppercase">{selectedPkg.id.startsWith("app") ? "App Development" : "Web Development"}</p>
                      <h2 className="font-['Unbounded'] text-lg font-bold text-white">{selectedPkg.name}</h2>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black text-white">${selectedPkg.price.toLocaleString()}</p>
                      <p className="text-[#00F0FF] text-xs">Deposit: ${selectedPkg.deposit.toLocaleString()}</p>
                    </div>
                  </div>

                  <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-6">Tell Us About Your Project</h2>
                  <form onSubmit={handleFormSubmit} className="glass-card rounded-3xl p-8 space-y-5" data-testid="booking-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Full Name *</label>
                        <input data-testid="booking-name" className={inputClass} value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Your name" />
                      </div>
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Email *</label>
                        <input data-testid="booking-email" type="email" className={inputClass} value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required placeholder="you@email.com" />
                      </div>
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Phone</label>
                        <input data-testid="booking-phone" className={inputClass} value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="(555) 123-4567" />
                      </div>
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Company / Brand</label>
                        <input data-testid="booking-company" className={inputClass} value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} placeholder="Optional" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Project Description *</label>
                      <textarea data-testid="booking-description" className={inputClass + " resize-none"} rows={5} value={form.project_description} onChange={(e) => setForm({...form, project_description: e.target.value})} required placeholder="Describe your project: What does the app/website do? Who is it for? Any specific features or integrations you need?" />
                    </div>
                    {error && <p className="text-red-400 text-sm">{error}</p>}
                    <button data-testid="booking-continue" type="submit" className="neon-btn w-full py-4 text-base">
                      Continue to Terms & Payment <ArrowRight className="w-4 h-4 inline ml-1" />
                    </button>
                  </form>
                </div>
              </motion.div>
            )}

            {/* STEP 3: Terms & Payment */}
            {step === "terms" && selectedPkg && (
              <motion.div key="terms" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
                <div className="max-w-2xl mx-auto">
                  {/* Order Summary */}
                  <div className="glass-card rounded-2xl p-6 mb-8">
                    <h3 className="font-['Unbounded'] text-lg font-bold text-white mb-4">Order Summary</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between"><span className="text-[#A0A0AB]">Package</span><span className="text-white font-semibold">{selectedPkg.name}</span></div>
                      <div className="flex justify-between"><span className="text-[#A0A0AB]">Timeline</span><span className="text-white">{selectedPkg.timeline}</span></div>
                      <div className="flex justify-between"><span className="text-[#A0A0AB]">Client</span><span className="text-white">{form.name} ({form.email})</span></div>
                      <div className="border-t border-white/10 pt-3 flex justify-between"><span className="text-[#A0A0AB]">Total Price</span><span className="text-white font-bold">${selectedPkg.price.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-[#A0A0AB]">Due Today (50% Deposit)</span><span className="text-[#00F0FF] font-bold text-lg">${selectedPkg.deposit.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-[#A0A0AB]">Due on Completion</span><span className="text-white">${selectedPkg.deposit.toLocaleString()}</span></div>
                    </div>
                  </div>

                  {/* Terms & Conditions */}
                  <div className="glass-card rounded-3xl p-8 mb-8" data-testid="service-terms">
                    <div className="flex items-center gap-3 mb-6">
                      <FileText className="w-6 h-6 text-[#00F0FF]" />
                      <h3 className="font-['Unbounded'] text-lg font-bold text-white">Terms & Conditions</h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto pr-2 text-[#A0A0AB] text-xs space-y-3 mb-6 scrollbar-thin">
                      <p className="font-semibold text-white">Service Agreement — Fortis Digital LLC</p>
                      <p><strong className="text-white">1. Scope of Work.</strong> Fortis Digital LLC ("Company") agrees to provide the digital development services as described in the selected package. The scope includes only the features listed in the package description. Any additional features or changes outside the original scope will be quoted separately.</p>
                      <p><strong className="text-white">2. Payment Terms.</strong> A 50% deposit is required to begin work. The remaining 50% is due upon project completion and client approval. All payments are non-refundable once work has commenced, except as outlined in Section 5.</p>
                      <p><strong className="text-white">3. Timeline.</strong> Estimated timelines are provided in good faith. Delays caused by the client (delayed feedback, content, or approvals) may extend the timeline. The Company will communicate any delays promptly.</p>
                      <p><strong className="text-white">4. Client Responsibilities.</strong> The client agrees to: (a) provide all necessary content, assets, and information in a timely manner; (b) respond to communications and review requests within 3 business days; (c) designate a single point of contact for the project.</p>
                      <p><strong className="text-white">5. Revisions & Refunds.</strong> Each package includes up to 2 rounds of revisions. Additional revisions will be billed at $150/hour. If the client cancels before development begins, a 75% refund of the deposit will be issued. No refunds after development has started.</p>
                      <p><strong className="text-white">6. Intellectual Property.</strong> Upon full payment, all custom code, designs, and assets created for the project are transferred to the client. The Company retains the right to showcase the project in its portfolio unless otherwise agreed in writing.</p>
                      <p><strong className="text-white">7. Third-Party Services.</strong> Costs for third-party services (hosting, domains, API subscriptions, app store fees) are the client's responsibility unless explicitly included in the package.</p>
                      <p><strong className="text-white">8. Warranty & Support.</strong> Post-launch support is included as specified in the package (30/60/90 days). This covers bug fixes for functionality delivered in the original scope. New features or enhancements are not covered under support.</p>
                      <p><strong className="text-white">9. Limitation of Liability.</strong> The Company's total liability shall not exceed the amount paid by the client for the project. The Company is not liable for any indirect, incidental, or consequential damages.</p>
                      <p><strong className="text-white">10. Confidentiality.</strong> Both parties agree to keep confidential any proprietary information shared during the project. This obligation survives the completion or termination of the project.</p>
                      <p><strong className="text-white">11. Termination.</strong> Either party may terminate the agreement with 14 days written notice. Upon termination, the client pays for all work completed to date.</p>
                      <p><strong className="text-white">12. Governing Law.</strong> This agreement is governed by the laws of the United States. Disputes shall be resolved through binding arbitration.</p>
                      <p className="text-[#A0A0AB]/60">By proceeding with payment, you acknowledge that you have read, understood, and agree to these terms.</p>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer mb-6">
                      <input
                        data-testid="service-agree-terms"
                        type="checkbox"
                        checked={agreesToTerms}
                        onChange={(e) => setAgreesToTerms(e.target.checked)}
                        className="accent-[#00F0FF] w-5 h-5 mt-0.5"
                      />
                      <span className="text-white text-sm">
                        I have read and agree to the <span className="text-[#00F0FF]">Terms & Conditions</span> and <Link to="/terms" className="text-[#00F0FF] hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-[#00F0FF] hover:underline">Privacy Policy</Link>.
                      </span>
                    </label>

                    {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

                    <button
                      data-testid="service-pay-deposit"
                      onClick={handlePayDeposit}
                      disabled={loading}
                      className="neon-btn w-full py-4 text-base flex items-center justify-center gap-3 disabled:opacity-50"
                    >
                      <CreditCard className="w-5 h-5" />
                      {loading ? "Processing..." : `Pay $${selectedPkg.deposit.toLocaleString()} Deposit`}
                    </button>

                    <div className="flex items-center justify-center gap-2 mt-4 text-[#A0A0AB] text-xs">
                      <Shield className="w-4 h-4" /> Secure payment via Stripe. 256-bit SSL encryption.
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </div>
      <Footer />
    </div>
  );
}
